<script setup lang="ts">
import { Form, Link } from "@inertiajs/vue3"
import { useTranslation } from "i18next-vue"
import PersistentLayout from "@/layouts/PersistentLayout.vue"
import Auth from "@/layouts/Auth.vue"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { sessions, passwords, signups } from "@/routes"

defineOptions({ layout: [PersistentLayout, Auth] })

const { t } = useTranslation()
const createRoute = sessions.create()

const errs = (error: string | string[] | undefined) =>
  error ? [error].flat() : []
</script>

<template>
  <div class="flex flex-col gap-6">
    <Card>
      <CardHeader class="text-center">
        <CardTitle as="h1" class="text-xl" data-testid="page-title">
          {{ t("session.title") }}
        </CardTitle>
        <CardDescription>
          {{ t("session.description") }}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form
          :action="createRoute.url"
          :method="createRoute.method"
          #default="{ errors, processing }"
        >
          <FieldGroup>
            <Field>
              <FieldLabel for="email_address">
                {{ t("session.email_address") }}
              </FieldLabel>
              <Input
                id="email_address"
                name="email_address"
                type="email"
                autocomplete="username"
                placeholder=""
                required
                :aria-invalid="Boolean(errors.emailAddress)"
              />
              <FieldError :errors="errs(errors.emailAddress)" />
            </Field>

            <Field>
              <div class="flex items-center">
                <FieldLabel for="password">
                  {{ t("session.password") }}
                </FieldLabel>
                <Link
                  :href="passwords.new().url"
                  class="ml-auto text-sm underline-offset-4 hover:underline"
                >
                  {{ t("session.forgot_password") }}
                </Link>
              </div>
              <Input
                id="password"
                name="password"
                type="password"
                autocomplete="current-password"
                required
                :aria-invalid="Boolean(errors.password)"
              />
              <FieldError :errors="errs(errors.password)" />
            </Field>

            <Field>
              <Button type="submit" :disabled="processing">
                {{ t("session.submit") }}
              </Button>
              <FieldDescription class="text-center">
                {{ t("session.have_invite_code") }}
                <Link :href="signups.new().url">
                  {{ t("session.sign_up") }}
                </Link>
              </FieldDescription>
            </Field>
          </FieldGroup>
        </Form>
      </CardContent>
    </Card>
  </div>
</template>
